import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useMe } from '../hooks/useMe'
import { useEvent, useEventCarriages, useUpdateEventCarriages, useUpdateCarriageTickets, useCarriageTicketOptions } from '../hooks/useEvents'
import { useEventFinancials } from '../hooks/useFinancials'
import {
  useTicketTypes,
  useEventTicketTypes,
  useCreateEventTicketType,
  usePatchEventTicketType,
  useDeleteEventTicketType
} from '../hooks/useTicketTypes'
import { formatGBP, parseGBP } from '../lib/money'
import { CARRIAGE_OPTIONS, SEAT_SELECTION_OPTIONS, carriageSeats, formationLetter, ticketKey, ticketInputFromKey, emTicketKey, meracTicketKey, type TicketKey } from '../lib/carriages'
import { downloadCsv, dateStamp, fileSlug } from '../lib/download'
import { Icon } from '../components/Icon'
import { useArchived } from '../contexts/ArchiveContext'
import type { EventTicketType, SeatSelection, CarriageInstance, CarriageTicketAssignment, CarriageTicketInput } from '../types'

// TicketingTab — per-event ticket build (Feature 4). A spreadsheet-like grid:
// add ticket types from the standard catalogue, set a per-event price, mark
// on-sale, allocation and notes. Export to CSV. Match each type to MERAC ticket
// classes to pull sales + £ (financial users). "Send to MERAC" is a phased
// placeholder — the push isn't built yet.
//
// Not financial-gated as a whole (ticketing/marketing use it); only the £-sales
// column is hasFinancial-gated, and inline within the grid.

interface Props {
  eventId: number
}

export function TicketingTab({ eventId }: Props) {
  const { data: me } = useMe()
  const archived = useArchived()
  const { data: event } = useEvent(eventId)
  const { data: catalogue } = useTicketTypes({ active: 'true' })
  const { data: rows, isLoading, error } = useEventTicketTypes(eventId)
  const createMut = useCreateEventTicketType(eventId)

  const [addId, setAddId] = useState<string>('')

  const canSeeSales = !!me?.hasFinancial

  // Live ticket classes already built + selling in MERAC for this event, pulled
  // straight from the synced sales (financial users only). Reuses the financials
  // rollup's per-class breakdown — no need for the user to rebuild them here.
  const { data: financials } = useEventFinancials(eventId, canSeeSales)
  const liveClasses = financials?.revenueByClass ?? []

  // Catalogue entries not already on this event.
  const available = useMemo(() => {
    const used = new Set((rows ?? []).map((r) => r.ticketTypeId))
    return (catalogue ?? []).filter((t) => !used.has(t.id))
  }, [catalogue, rows])

  function handleAdd() {
    const id = Number(addId)
    if (!Number.isInteger(id) || id <= 0) return
    const nextSort = (rows?.length ?? 0) * 10
    // Pre-fill the per-event price from the catalogue's standard price (mig 068).
    const cat = (catalogue ?? []).find((t) => t.id === id)
    createMut.mutate(
      { ticketTypeId: id, pricePence: cat?.defaultPricePence ?? null, onSale: true, allocation: null, notes: null, sortOrder: nextSort },
      { onSuccess: () => setAddId('') }
    )
  }

  function handleExport() {
    const header = ['Ticket type', 'Category', 'Concession', 'Price', 'On sale', 'Allocation', 'Notes']
    const body = (rows ?? []).map((r): (string | number)[] => [
      r.name,
      r.category,
      r.isConcession ? 'Yes' : 'No',
      r.pricePence == null ? '' : formatGBP(r.pricePence),
      r.onSale ? 'Yes' : 'No',
      r.allocation == null ? '' : r.allocation,
      r.notes ?? ''
    ])
    const name = `nymr-tickets-${fileSlug(event?.name ?? 'event')}-${dateStamp()}.csv`
    downloadCsv(name, [header, ...body])
  }

  if (isLoading) return <div className="text-ink-soft text-sm font-mono">Loading ticket types…</div>
  if (error) {
    return (
      <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
        <div className="font-semibold mb-1">Could not load ticket types</div>
        <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
      </div>
    )
  }

  const list = rows ?? []

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-xl">Ticketing</h2>
          <p className="text-sm text-ink-soft max-w-2xl">
            Build this event's ticket types from the standard catalogue, set prices,
            and export to a spreadsheet. Match each type to MERAC to pull live sales.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleExport}
            disabled={list.length === 0}
            className="px-3 py-1.5 text-sm font-semibold rounded border border-line text-ink-soft hover:text-green hover:border-green transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <Icon name="download" size={14} /> Export CSV
          </button>
          <button
            type="button"
            disabled
            title="Coming soon — this will push the ticket types below directly into MERAC. For now, build them in MERAC manually and Match them here."
            className="px-3 py-1.5 text-sm font-semibold rounded border border-line text-ink-soft/50 inline-flex items-center gap-1.5 cursor-not-allowed"
          >
            <Icon name="ticket" size={14} /> Send to MERAC
          </button>
        </div>
      </div>

      {/* Selling now in MERAC — the ticket classes already built + selling in the
          connected MERAC event, pulled live so the user doesn't rebuild them.
          Financial-gated (reuses the financials per-class breakdown). */}
      {canSeeSales && liveClasses.length > 0 && (
        <div className="bg-white border border-green/40 rounded overflow-hidden">
          <div className="px-4 py-2.5 border-b border-line bg-green-soft/50 flex items-center gap-2 flex-wrap">
            <Icon name="pound" size={15} className="text-green" />
            <h3 className="text-sm font-bold text-green uppercase tracking-wide">Selling now in MERAC</h3>
            <span className="text-xs text-ink-soft">pulled live — already built &amp; selling in MERAC</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-ink-soft">
                  <th className="px-3 py-2 font-semibold">Ticket type</th>
                  <th className="px-3 py-2 font-semibold text-right">Tickets</th>
                  <th className="px-3 py-2 font-semibold text-right">Price / ticket</th>
                  <th className="px-3 py-2 font-semibold text-right">Total sales</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {liveClasses.map((c) => {
                  const pricePer = c.quantitySold > 0 ? Math.round(c.grossPence / c.quantitySold) : null
                  return (
                    <tr key={c.ticketClassMeracId} className="hover:bg-parchment/30">
                      <td className="px-3 py-2 font-semibold">{c.ticketClassName}</td>
                      <td className="px-3 py-2 text-right font-mono">{c.quantitySold.toLocaleString('en-GB')}</td>
                      <td className="px-3 py-2 text-right font-mono">{pricePer != null ? formatGBP(pricePer) : '—'}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold">{formatGBP(c.grossPence)}</td>
                    </tr>
                  )
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-line font-semibold">
                  <td className="px-3 py-2">Total</td>
                  <td className="px-3 py-2 text-right font-mono">
                    {liveClasses.reduce((s, c) => s + c.quantitySold, 0).toLocaleString('en-GB')}
                  </td>
                  <td></td>
                  <td className="px-3 py-2 text-right font-mono">
                    {formatGBP(liveClasses.reduce((s, c) => s + c.grossPence, 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="px-4 py-2 text-xs text-ink-soft border-t border-line">
            These ticket types are already built and selling in MERAC — you don't need to rebuild them below.
            {financials?.netPending && ' Figures are gross; net-of-refunds is still syncing.'}
          </div>
        </div>
      )}

      {/* Add control (hidden on archived events — read-only) */}
      {!archived && (
      <div className="flex items-center gap-2 bg-white border border-line rounded p-3">
        <span className="text-xs uppercase tracking-wide font-semibold text-ink-soft">Add ticket type</span>
        <select
          value={addId}
          onChange={(e) => setAddId(e.target.value)}
          className="border border-line rounded px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-green/30 focus:border-green flex-1 max-w-md"
        >
          <option value="">Choose from the catalogue…</option>
          {available.map((t) => (
            <option key={t.id} value={t.id}>{t.name}{t.isConcession ? ' (concession)' : ''}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={handleAdd}
          disabled={!addId || createMut.isPending}
          className="px-3 py-1.5 text-sm font-semibold rounded bg-green text-parchment hover:bg-green/90 disabled:opacity-50"
        >
          {createMut.isPending ? 'Adding…' : '+ Add'}
        </button>
        {available.length === 0 && catalogue && catalogue.length > 0 && (
          <span className="text-xs text-ink-soft">All catalogue types added.</span>
        )}
      </div>
      )}
      {!archived && createMut.error && (
        <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-2 text-xs">
          {(createMut.error as Error).message}
        </div>
      )}

      {/* Grid */}
      {list.length === 0 ? (
        <div className="bg-white border border-line rounded p-10 text-center">
          <h3 className="font-display text-lg mb-1">No ticket types on this event yet</h3>
          <p className="text-sm text-ink-soft max-w-md mx-auto">
            Add ticket types from the catalogue above. Manage the standard catalogue
            under Resources → Ticket types.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-line rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-ink-soft">
                <th className="px-3 py-2 font-semibold">Ticket type</th>
                <th className="px-3 py-2 font-semibold">Price</th>
                <th className="px-3 py-2 font-semibold">On sale</th>
                <th className="px-3 py-2 font-semibold">Allocation</th>
                <th className="px-3 py-2 font-semibold">Notes</th>
                <th className="px-3 py-2 font-semibold w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {list.map((r) => (
                <TicketRow
                  key={r.id}
                  eventId={eventId}
                  row={r}
                  archived={archived}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CarriagesCard eventId={eventId} archived={archived} />

    </div>
  )
}

// ---- Inline-editable row ---------------------------------------------------

function TicketRow({
  eventId, row, archived
}: {
  eventId: number
  row: EventTicketType
  archived: boolean
}) {
  const patchMut = usePatchEventTicketType(eventId)
  const deleteMut = useDeleteEventTicketType(eventId)

  const [price, setPrice] = useState(row.pricePence == null ? '' : formatGBP(row.pricePence))
  const [allocation, setAllocation] = useState(row.allocation == null ? '' : String(row.allocation))
  const [notes, setNotes] = useState(row.notes ?? '')

  function commit(overrides: Partial<{ pricePence: number | null; onSale: boolean; allocation: number | null; notes: string | null }>) {
    patchMut.mutate({
      id: row.id,
      body: {
        pricePence: overrides.pricePence !== undefined ? overrides.pricePence : row.pricePence,
        onSale: overrides.onSale !== undefined ? overrides.onSale : row.onSale,
        allocation: overrides.allocation !== undefined ? overrides.allocation : row.allocation,
        notes: overrides.notes !== undefined ? overrides.notes : row.notes,
        sortOrder: row.sortOrder
      }
    })
  }

  function commitPrice() {
    const trimmed = price.trim()
    const pence = trimmed === '' ? null : parseGBP(trimmed)
    if (trimmed !== '' && pence == null) {
      setPrice(row.pricePence == null ? '' : formatGBP(row.pricePence)) // revert bad input
      return
    }
    if (pence !== row.pricePence) commit({ pricePence: pence })
    else setPrice(pence == null ? '' : formatGBP(pence))
  }

  function commitAllocation() {
    const trimmed = allocation.trim()
    const n = trimmed === '' ? null : Number(trimmed)
    if (trimmed !== '' && (!Number.isInteger(n) || (n as number) < 0)) {
      setAllocation(row.allocation == null ? '' : String(row.allocation))
      return
    }
    if (n !== row.allocation) commit({ allocation: n })
  }

  function commitNotes() {
    const next = notes.trim() || null
    if (next !== row.notes) commit({ notes: next })
  }

  function handleDelete() {
    if (window.confirm(`Remove "${row.name}" from this event's ticketing?`)) {
      deleteMut.mutate(row.id)
    }
  }

  return (
    <tr className="hover:bg-parchment/30">
      <td className="px-3 py-2">
        <div className="font-semibold">{row.name}</div>
        <div className="text-xs text-ink-soft">{row.category}{row.isConcession ? ' · concession' : ''}</div>
      </td>
      <td className="px-3 py-2">
        <input
          type="text"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          onBlur={commitPrice}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
          placeholder="£0.00"
          disabled={archived}
          readOnly={archived}
          className="w-24 border border-line rounded px-2 py-1 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-green/30 focus:border-green disabled:bg-parchment/40 disabled:text-ink-soft"
        />
      </td>
      <td className="px-3 py-2">
        <input
          type="checkbox"
          checked={row.onSale}
          disabled={archived}
          onChange={(e) => commit({ onSale: e.target.checked })}
        />
      </td>
      <td className="px-3 py-2">
        <input
          type="text"
          inputMode="numeric"
          value={allocation}
          onChange={(e) => setAllocation(e.target.value)}
          onBlur={commitAllocation}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
          placeholder="—"
          disabled={archived}
          readOnly={archived}
          className="w-20 border border-line rounded px-2 py-1 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-green/30 focus:border-green disabled:bg-parchment/40 disabled:text-ink-soft"
        />
      </td>
      <td className="px-3 py-2">
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={commitNotes}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
          placeholder="—"
          disabled={archived}
          readOnly={archived}
          className="w-full min-w-[8rem] border border-line rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-green/30 focus:border-green disabled:bg-parchment/40 disabled:text-ink-soft"
        />
      </td>
      <td className="px-3 py-2 text-right">
        {!archived && (
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleteMut.isPending}
            aria-label={`Remove ${row.name}`}
            className="text-ink-soft hover:text-oxblood text-lg leading-none disabled:opacity-50"
          >
            ×
          </button>
        )}
      </td>
    </tr>
  )
}

// ---- Carriages card --------------------------------------------------------
// The event's carriage FORMATION, north to south (Chris, 2026-09-17/18), and
// the TICKET ASSIGNMENTS on each carriage (2026-09-18). Three columns:
//   • Available carriages — a PALETTE (the same type can be added more than once);
//   • Selected carriages — the formation between NORTH (top) and SOUTH (bottom),
//     every position lettered from the north end (A, B, C …), each with its
//     seats, a Total seats row, and an "Assign ticket types" button per carriage;
//   • Carriage Ticket Assignments — per carriage, the ticket types assigned with
//     a quantity of seats each and "n of S allocated" (never more than the
//     carriage's seats), then every ticket type's total across the formation with
//     the estimated maximum revenue (quantity × price), and the total seats
//     allocated.
// Drag across to add (dropping at a position), drag within the formation to
// re-order, drag back or ✕ to remove; Add / ▲ ▼ click fallbacks. Every change
// saves at once — the formation with each carriage's id, so assignments follow a
// carriage when it moves. The Sync Hub Ticketing card prints all of it, read-only.
//
// A ticket on a carriage is EITHER a ticket-build type OR a MERAC ticket class
// already selling on the event (db/089) — Chris: "I don't want to have to create
// a 'ticket type' in Event Manager for a MERAC ticket that's already been
// created". Everything is keyed by `ticketKey` ("em:<id>" | "merac:<id>"); the
// names and prices ride on the payload rows (a MERAC class's price = what it has
// sold at so far, financial users only), so the card needs no ticket-type list.
type CarriageDrag = { from: 'available'; code: string } | { from: 'selected'; index: number }

// A carriage not yet saved has a temporary negative id until the server answers.
let tempCarriageId = -1

// Insert a NEW instance of `code` at slot `index` (0 = north end).
export function insertCarriage(list: CarriageInstance[], code: string, index: number): CarriageInstance[] {
  const at = Math.max(0, Math.min(index, list.length))
  const fresh: CarriageInstance = { id: tempCarriageId--, code, sortOrder: at, seats: carriageSeats(code) ?? null, tickets: [] }
  return [...list.slice(0, at), fresh, ...list.slice(at)]
}

// Move the instance at `from` to slot `index`. An item that already sits above
// the slot shifts the slot up by one, so dropping "just below" the next item
// lands where the indicator showed.
export function moveCarriage<T>(list: T[], from: number, index: number): T[] {
  if (from < 0 || from >= list.length) return list
  const without = list.filter((_, i) => i !== from)
  const at = from < index ? index - 1 : index
  const clamped = Math.max(0, Math.min(at, without.length))
  return [...without.slice(0, clamped), list[from]!, ...without.slice(clamped)]
}

function carriageLabel(inst: CarriageInstance, index: number): string {
  return `${formationLetter(index)} ${inst.code}`
}

// ---- Assign tickets dialog -------------------------------------------------
// Lists what may sit on the carriage: first the MERAC ticket classes already
// selling on the event (no Event Manager ticket type needed), then the ticket
// build. `current` = the carriage's assignments today, so a ticket that has since
// left both lists can still be unticked. Save returns the ticked keys.
function AssignTicketsDialog({
  eventId, label, seats, current, saving, error, onSave, onClose
}: {
  eventId: number
  label: string
  seats: number | null
  current: CarriageTicketAssignment[]
  saving: boolean
  error: string | null
  onSave: (keys: TicketKey[]) => void
  onClose: () => void
}) {
  const { data: options, isLoading, error: loadError } = useCarriageTicketOptions(eventId)
  const [picked, setPicked] = useState<Set<TicketKey>>(() => new Set(current.map(ticketKey)))
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  function toggle(key: TicketKey) {
    setPicked((prev) => { const next = new Set(prev); if (next.has(key)) next.delete(key); else next.add(key); return next })
  }
  const merac = options?.merac ?? []
  const em = options?.em ?? []
  // Assigned today but no longer offered (the MERAC sync no longer lists the
  // class, or the ticket-build type went): still shown so they can be unticked.
  const offered = new Set<TicketKey>([...merac.map((m) => meracTicketKey(m.ticketClassMeracId)), ...em.map((t) => emTicketKey(t.eventTicketTypeId))])
  const orphans = current.filter((t) => !offered.has(ticketKey(t)))
  const nothing = !isLoading && !loadError && merac.length === 0 && em.length === 0 && orphans.length === 0
  const row = 'flex items-center gap-3 text-sm cursor-pointer'
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label={`Assign ticket types to ${label}`} className="w-full max-w-lg rounded border border-line bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-line bg-parchment/40">
          <h3 className="text-sm font-bold uppercase tracking-wide">Assign ticket types</h3>
          <p className="text-xs text-ink-soft mt-0.5">{label}{seats != null ? ` · ${seats} seats` : ''} — tick every ticket that can be sold on this carriage. The same ticket may sit on several carriages.</p>
        </div>
        <div className="p-4 max-h-[60vh] overflow-y-auto space-y-4">
          {isLoading && <p className="text-sm text-ink-soft">Loading the tickets selling in MERAC…</p>}
          {loadError && <p className="text-sm text-oxblood">Could not load the tickets: {(loadError as Error).message}</p>}
          {nothing && (
            <p className="text-sm text-ink-soft">
              Nothing to assign yet — link the event to its MERAC event so its ticket classes appear here, or add ticket types in the ticket build above.
            </p>
          )}
          {merac.length > 0 && (
            <section data-ticket-group="merac">
              <div className="flex items-baseline gap-2 mb-2 flex-wrap">
                <h4 className="text-xs font-bold uppercase tracking-wide text-green">Selling now in MERAC</h4>
                <span className="text-[11px] text-ink-soft">already built and selling — no ticket type needed in Event Manager</span>
              </div>
              <ul className="flex flex-col gap-2">
                {merac.map((m) => {
                  const key = meracTicketKey(m.ticketClassMeracId)
                  return (
                    <li key={key}>
                      <label className={row}>
                        <input type="checkbox" checked={picked.has(key)} onChange={() => toggle(key)} data-ticket-merac={m.ticketClassMeracId} />
                        <span className="flex-1 min-w-0">
                          <span className="font-semibold">{m.name}</span>
                          <MeracTag />
                        </span>
                        <span className="text-ink-soft tabular-nums whitespace-nowrap">
                          {m.pricePence != null ? formatGBP(m.pricePence) : '—'}
                          {m.sold != null && <span className="text-[11px]"> · {m.sold.toLocaleString('en-GB')} sold</span>}
                        </span>
                      </label>
                    </li>
                  )
                })}
              </ul>
            </section>
          )}
          {em.length > 0 && (
            <section data-ticket-group="em">
              <div className="flex items-baseline gap-2 mb-2 flex-wrap">
                <h4 className="text-xs font-bold uppercase tracking-wide text-ink-soft">Ticket build (Event Manager)</h4>
                <span className="text-[11px] text-ink-soft">the ticket types planned on this event</span>
              </div>
              <ul className="flex flex-col gap-2">
                {em.map((t) => {
                  const key = emTicketKey(t.eventTicketTypeId)
                  return (
                    <li key={key}>
                      <label className={row}>
                        <input type="checkbox" checked={picked.has(key)} onChange={() => toggle(key)} data-ticket-type={t.eventTicketTypeId} />
                        <span className="flex-1 min-w-0">
                          <span className="font-semibold">{t.name}</span>{' '}
                          <span className="text-ink-soft">({t.category})</span>
                        </span>
                        <span className="text-ink-soft tabular-nums">{t.pricePence != null ? formatGBP(t.pricePence) : 'no price'}</span>
                      </label>
                    </li>
                  )
                })}
              </ul>
            </section>
          )}
          {orphans.length > 0 && (
            <section data-ticket-group="orphan">
              <h4 className="text-xs font-bold uppercase tracking-wide text-ink-soft mb-2">Assigned, but no longer listed</h4>
              <ul className="flex flex-col gap-2">
                {orphans.map((t) => {
                  const key = ticketKey(t)
                  return (
                    <li key={key}>
                      <label className={row}>
                        <input type="checkbox" checked={picked.has(key)} onChange={() => toggle(key)} data-ticket-orphan={key} />
                        <span className="flex-1 min-w-0"><span className="font-semibold">{t.name}</span>{t.source === 'merac' && <MeracTag />}</span>
                        <span className="text-[11px] text-ink-soft">{t.source === 'merac' ? 'not in the synced MERAC sales any more' : 'removed from the ticket build'}</span>
                      </label>
                    </li>
                  )
                })}
              </ul>
            </section>
          )}
        </div>
        {error && <div className="px-4 py-2 text-xs text-oxblood border-t border-line">{error}</div>}
        <div className="px-4 py-3 border-t border-line flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 rounded border border-line text-sm hover:bg-parchment/60">Cancel</button>
          <button type="button" onClick={() => onSave(Array.from(picked))} disabled={saving || isLoading || nothing} className="px-3 py-1.5 rounded bg-green text-white text-sm font-semibold disabled:opacity-60" data-assign-save>
            {saving ? 'Saving…' : 'Save assignments'}
          </button>
        </div>
      </div>
    </div>
  )
}

// A small "MERAC" tag on a ticket that is a MERAC ticket class rather than a
// ticket-build type — the Sync Hub card prints the same tag.
function MeracTag() {
  return (
    <span
      className="ml-1.5 inline-block align-middle rounded bg-parchment px-1 py-px text-[9.5px] font-bold uppercase tracking-wide text-ink-soft"
      title="A MERAC ticket class selling on this event — assigned straight from the synced sales, no Event Manager ticket type"
      data-source="merac"
    >
      MERAC
    </span>
  )
}

export function CarriagesCard({
  eventId, archived
}: {
  eventId: number
  archived: boolean
}) {
  const { data, isLoading, error: loadError } = useEventCarriages(eventId)
  const updateMut = useUpdateEventCarriages(eventId)
  const ticketsMut = useUpdateCarriageTickets(eventId)
  const [sel, setSel] = useState<CarriageInstance[]>([])
  // Re-sync local state when the server value settles (load / a mutation's response).
  useEffect(() => { if (data) setSel(data.carriages) }, [data])
  // "Seats" pills (2026-09-18): No Seat Selection | Sets As Defined — saved at once.
  const [seatSel, setSeatSel] = useState<SeatSelection | null>(null)
  useEffect(() => { if (data) setSeatSel(data.seatSelection) }, [data])
  function chooseSeats(v: SeatSelection) {
    if (archived || v === seatSel) return
    setSeatSel(v)
    updateMut.mutate({ seatSelection: v })
  }
  // Seats column + total for the formation (catalogue seat counts; a code no
  // longer in the catalogue counts as unknown and is said so).
  const totalSeats = sel.reduce((a, c) => a + (carriageSeats(c.code) ?? 0), 0)
  const unknownSeats = sel.filter((c) => carriageSeats(c.code) === undefined).length

  const [drag, setDrag] = useState<CarriageDrag | null>(null)
  const [overSlot, setOverSlot] = useState<number | null>(null)     // insertion slot in the formation, 0..sel.length
  const [overAvailable, setOverAvailable] = useState(false)
  const listRef = useRef<HTMLOListElement>(null)

  const byCode = useMemo(() => new Map(CARRIAGE_OPTIONS.map((c) => [c.code, c])), [])
  const countOf = (code: string) => sel.filter((c) => c.code === code).length

  function commit(next: CarriageInstance[]) {
    setSel(next)
    updateMut.mutate({ carriages: next.map((c) => (c.id > 0 ? { id: c.id, code: c.code } : { code: c.code })) })
  }
  function add(code: string) { commit(insertCarriage(sel, code, sel.length)) }
  function removeAt(index: number) { commit(sel.filter((_, i) => i !== index)) }
  function moveBy(index: number, delta: number) {
    const j = index + delta
    if (index < 0 || index >= sel.length || j < 0 || j >= sel.length) return
    const next = [...sel]
    const [item] = next.splice(index, 1)
    next.splice(j, 0, item!)
    commit(next)
  }

  // Which slot the pointer is over: above an item's midpoint = before it.
  function slotFromPointer(clientY: number): number {
    const items = Array.from(listRef.current?.querySelectorAll<HTMLElement>('[data-index]') ?? [])
    for (let i = 0; i < items.length; i++) {
      const r = items[i]!.getBoundingClientRect()
      if (clientY < r.top + r.height / 2) return i
    }
    return items.length
  }
  const endDrag = () => { setDrag(null); setOverSlot(null); setOverAvailable(false) }
  const startDrag = (payload: CarriageDrag) => (e: React.DragEvent) => {
    if (archived) { e.preventDefault(); return }
    setDrag(payload)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', payload.from === 'available' ? payload.code : sel[payload.index]?.code ?? '')
  }
  const onFormationDragOver = (e: React.DragEvent) => {
    if (!drag) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setOverSlot(slotFromPointer(e.clientY))
    setOverAvailable(false)
  }
  const onFormationDragLeave = (e: React.DragEvent) => {
    if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node | null)) setOverSlot(null)
  }
  const onFormationDrop = (e: React.DragEvent) => {
    if (!drag) return
    e.preventDefault()
    const slot = overSlot ?? slotFromPointer(e.clientY)
    commit(drag.from === 'available' ? insertCarriage(sel, drag.code, slot) : moveCarriage(sel, drag.index, slot))
    endDrag()
  }
  const onAvailableDragOver = (e: React.DragEvent) => {
    if (!drag || drag.from !== 'selected') return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setOverAvailable(true)
    setOverSlot(null)
  }
  const onAvailableDragLeave = (e: React.DragEvent) => {
    if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node | null)) setOverAvailable(false)
  }
  const onAvailableDrop = (e: React.DragEvent) => {
    if (!drag || drag.from !== 'selected') return
    e.preventDefault()
    removeAt(drag.index)
    endDrag()
  }

  // ---- ticket assignments ----------------------------------------------
  // Everything is keyed by ticketKey ("em:<id>" | "merac:<id>"); the names and
  // prices come back on the payload rows, so a MERAC class needs no ticket type.
  const [assigning, setAssigning] = useState<number | null>(null)   // index of the carriage whose dialog is open
  const [assignError, setAssignError] = useState<string | null>(null)
  // Quantity drafts while typing, keyed "carriageId:ticketKey".
  const [qtyDraft, setQtyDraft] = useState<Record<string, string>>({})
  const [qtyNote, setQtyNote] = useState<string | null>(null)

  const toInputs = (tickets: CarriageTicketAssignment[]): CarriageTicketInput[] =>
    tickets.map((t) => ticketInputFromKey(ticketKey(t), t.quantity))

  function saveAssignments(inst: CarriageInstance, keys: TicketKey[]) {
    if (inst.id <= 0) { setAssignError('This carriage is still saving — try again in a moment.'); return }
    const tickets: CarriageTicketInput[] = keys.map((key) => ticketInputFromKey(key, inst.tickets.find((t) => ticketKey(t) === key)?.quantity ?? 0))
    setAssignError(null)
    ticketsMut.mutate({ carriageId: inst.id, tickets }, {
      onSuccess: () => setAssigning(null),
      onError: (e) => setAssignError((e as Error).message),
    })
  }
  function commitQuantity(inst: CarriageInstance, key: TicketKey, rawValue: string) {
    const draftKey = `${inst.id}:${key}`
    const parsed = Math.floor(Number(rawValue))
    const current = inst.tickets.find((t) => ticketKey(t) === key)?.quantity ?? 0
    let qty = Number.isFinite(parsed) && parsed >= 0 ? parsed : current
    const others = inst.tickets.filter((t) => ticketKey(t) !== key).reduce((a, t) => a + t.quantity, 0)
    if (inst.seats != null && others + qty > inst.seats) {
      qty = Math.max(0, inst.seats - others)
      setQtyNote(`${carriageLabel(inst, sel.indexOf(inst))} has ${inst.seats} seats — ${others} already allocated to other tickets, so this was capped at ${qty}.`)
    } else {
      setQtyNote(null)
    }
    setQtyDraft((d) => { const n = { ...d }; delete n[draftKey]; return n })
    if (qty === current) return
    const tickets = inst.tickets.map((t) => (ticketKey(t) === key ? { ...t, quantity: qty } : t))
    // optimistic local update so the sub-total moves at once
    setSel((prev) => prev.map((c) => (c.id === inst.id ? { ...c, tickets } : c)))
    ticketsMut.mutate({ carriageId: inst.id, tickets: toInputs(tickets) }, { onError: (e) => setQtyNote((e as Error).message) })
  }
  function removeAssignment(inst: CarriageInstance, key: TicketKey) {
    const tickets = inst.tickets.filter((t) => ticketKey(t) !== key)
    setSel((prev) => prev.map((c) => (c.id === inst.id ? { ...c, tickets } : c)))
    ticketsMut.mutate({ carriageId: inst.id, tickets: toInputs(tickets) }, { onError: (e) => setQtyNote((e as Error).message) })
  }

  // Totals across the formation per ticket, in the order first met north → south.
  // Price: the ticket build's, or for a MERAC class the average it has sold at.
  const ticketTotals = useMemo(() => {
    const totals = new Map<TicketKey, { key: TicketKey; name: string; source: CarriageTicketAssignment['source']; qty: number; price: number | null }>()
    for (const c of sel) for (const t of c.tickets) {
      const key = ticketKey(t)
      const cur = totals.get(key)
      if (cur) { cur.qty += t.quantity; if (cur.price == null) cur.price = t.pricePence }
      else totals.set(key, { key, name: t.name, source: t.source, qty: t.quantity, price: t.pricePence })
    }
    return Array.from(totals.values()).map((t) => ({ ...t, revenue: t.price != null ? t.qty * t.price : null }))
  }, [sel])
  const seatsAllocated = ticketTotals.reduce((a, t) => a + t.qty, 0)
  const revenueTotal = ticketTotals.reduce((a, t) => a + (t.revenue ?? 0), 0)
  const anyPriced = ticketTotals.some((t) => t.revenue != null)
  const withAssignments = sel.map((c, i) => ({ c, i })).filter(({ c }) => c.tickets.length > 0)

  const endLabel = 'text-center text-[11px] font-bold tracking-[0.22em] text-green py-1 select-none'
  const iconBtn = 'px-1 text-ink-soft hover:text-green disabled:opacity-30 disabled:hover:text-ink-soft leading-none'
  const saveError = (updateMut.error as Error | null)?.message ?? (loadError as Error | null)?.message ?? null

  return (
    <div className="bg-white border border-line rounded overflow-hidden">
      <div className="px-4 py-2.5 border-b border-line bg-parchment/40 flex items-center gap-2 flex-wrap">
        <h3 className="text-sm font-bold uppercase tracking-wide">Carriages</h3>
        <span className="text-xs text-ink-soft">
          the formation for building the MERAC sessions, north to south, with the ticket types allocated to each carriage — shown on the Sync Hub Ticketing card
        </span>
        {(updateMut.isPending || ticketsMut.isPending) && <span className="text-xs text-ink-soft">saving…</span>}
        <div className="ml-auto flex items-center gap-2" role="radiogroup" aria-label="Seats">
          <span className="text-xs font-semibold uppercase tracking-wide text-ink-soft">Seats</span>
          {SEAT_SELECTION_OPTIONS.map((o) => {
            const on = seatSel === o.value
            return (
              <button
                key={o.value}
                type="button"
                role="radio"
                aria-checked={on}
                disabled={archived}
                title={o.hint}
                onClick={() => chooseSeats(o.value)}
                className={`px-3 py-1 rounded-full border text-xs font-semibold transition-colors ${on ? 'bg-green text-white border-green' : 'bg-white text-ink-soft border-line hover:border-green hover:text-green'} disabled:opacity-60 disabled:hover:border-line disabled:hover:text-ink-soft`}
              >
                {o.label}
              </button>
            )
          })}
        </div>
      </div>

      {isLoading && !data && <div className="p-4 text-sm text-ink-soft">Loading the formation…</div>}

      <div className="p-4 grid gap-4 md:grid-cols-2 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1fr)_minmax(0,1.35fr)]">
        {/* Available carriages — a palette; each can be added more than once */}
        <section
          aria-label="Available carriages"
          onDragOver={onAvailableDragOver}
          onDragLeave={onAvailableDragLeave}
          onDrop={onAvailableDrop}
          className={`rounded border p-3 min-h-[140px] transition-colors ${overAvailable ? 'border-green bg-green-soft/40' : 'border-line'}`}
        >
          <div className="text-xs font-semibold uppercase tracking-wide text-ink-soft mb-2">Available carriages</div>
          <ul className="flex flex-col gap-2">
            {CARRIAGE_OPTIONS.map((c) => {
              const n = countOf(c.code)
              return (
                <li
                  key={c.code}
                  data-available={c.code}
                  draggable={!archived}
                  onDragStart={startDrag({ from: 'available', code: c.code })}
                  onDragEnd={endDrag}
                  className={`flex items-center gap-2 rounded border border-line bg-white px-3 py-2 text-sm ${archived ? '' : 'cursor-grab active:cursor-grabbing hover:border-green'} ${drag?.from === 'available' && drag.code === c.code ? 'opacity-50' : ''}`}
                >
                  {!archived && <span aria-hidden className="text-ink-soft select-none">⋮⋮</span>}
                  <span className="flex-1 min-w-0">
                    <span className="block font-semibold leading-tight">{c.code}</span>
                    <span className="block text-[11px] text-ink-soft truncate" title={c.desc}>{c.desc}</span>
                  </span>
                  {n > 0 && (
                    <span className="text-[11px] font-semibold text-green whitespace-nowrap" title={`${n} in the formation`} data-in-formation={n}>
                      ×{n} in formation
                    </span>
                  )}
                  {!archived && (
                    <button type="button" onClick={() => add(c.code)} className="text-xs font-semibold text-green hover:underline whitespace-nowrap" title="Add one to the formation (at the south end)">
                      Add ›
                    </button>
                  )}
                </li>
              )
            })}
          </ul>
          {!archived && (
            <div className="mt-3 text-[11px] text-ink-soft">
              Drag a carriage into the formation, or click Add — a carriage can be added more than once. Drag one back here to remove it.
            </div>
          )}
        </section>

        {/* Selected carriages — the formation, lettered from the north end */}
        <section aria-label="Selected carriages" className="rounded border border-line p-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-ink-soft mb-2">Selected carriages — formation</div>
          <div className={`${endLabel} border-b border-dashed border-line`}>NORTH</div>
          <ol
            ref={listRef}
            onDragOver={onFormationDragOver}
            onDragLeave={onFormationDragLeave}
            onDrop={onFormationDrop}
            className="flex flex-col gap-2 py-2 min-h-[72px]"
          >
            {sel.length === 0 && !drag && (
              <li className="text-xs text-ink-soft italic text-center py-3">
                No carriages selected yet{archived ? '.' : ' — drag one in from the left, or click Add.'}
              </li>
            )}
            {sel.map((inst, i) => {
              const letter = formationLetter(i)
              const dragging = drag?.from === 'selected' && drag.index === i
              const label = carriageLabel(inst, i)
              return (
                <Fragment key={inst.id}>
                  {drag && overSlot === i && <li aria-hidden className="h-1 rounded bg-green" />}
                  <li
                    data-carriage={inst.code}
                    data-carriage-id={inst.id}
                    data-index={i}
                    data-letter={letter}
                    draggable={!archived}
                    onDragStart={startDrag({ from: 'selected', index: i })}
                    onDragEnd={endDrag}
                    className={`flex items-center gap-2 rounded border border-line bg-parchment/40 px-3 py-2 text-sm ${archived ? '' : 'cursor-grab active:cursor-grabbing hover:border-green'} ${dragging ? 'opacity-50' : ''}`}
                  >
                    <span className="min-w-[1.5rem] text-center text-xs font-bold text-green bg-green-soft rounded px-1 py-0.5 select-none" title="Position from the north end">{letter}</span>
                    {!archived && <span aria-hidden className="text-ink-soft select-none">⋮⋮</span>}
                    <span className="flex-1 min-w-0">
                      <span className="block font-semibold leading-tight">{inst.code}</span>
                      {byCode.get(inst.code) && <span className="block text-[11px] text-ink-soft truncate" title={byCode.get(inst.code)!.desc}>{byCode.get(inst.code)!.desc}</span>}
                    </span>
                    <span className="w-10 text-right text-sm tabular-nums" data-seats={carriageSeats(inst.code) ?? ''} title="Seats">
                      {carriageSeats(inst.code) ?? '—'}
                    </span>
                    {!archived && (
                      <span className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => { setAssignError(null); setAssigning(i) }}
                          disabled={inst.id <= 0}
                          aria-label={`Assign ticket types to ${label}`}
                          title={inst.tickets.length > 0 ? `${inst.tickets.length} ticket type${inst.tickets.length === 1 ? '' : 's'} assigned — click to change` : 'Assign ticket types to this carriage'}
                          className={`flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] font-semibold ${inst.tickets.length > 0 ? 'border-green text-green bg-green-soft/60' : 'border-line text-ink-soft hover:border-green hover:text-green'} disabled:opacity-40`}
                          data-assign-count={inst.tickets.length}
                        >
                          <Icon name="ticket" size={12} />
                          {inst.tickets.length > 0 ? inst.tickets.length : ''}
                        </button>
                        <button type="button" onClick={() => moveBy(i, -1)} disabled={i === 0} aria-label={`Move ${label} towards the north end`} title="Move north" className={iconBtn}>▲</button>
                        <button type="button" onClick={() => moveBy(i, 1)} disabled={i === sel.length - 1} aria-label={`Move ${label} towards the south end`} title="Move south" className={iconBtn}>▼</button>
                        <button type="button" onClick={() => removeAt(i)} aria-label={`Remove ${label} from the formation`} title={inst.tickets.length > 0 ? 'Remove (its ticket assignments go with it)' : 'Remove'} className="px-1 text-ink-soft hover:text-oxblood leading-none">✕</button>
                      </span>
                    )}
                    {archived && inst.tickets.length > 0 && (
                      <span className="text-[11px] text-green font-semibold" title="Ticket types assigned"><Icon name="ticket" size={12} /> {inst.tickets.length}</span>
                    )}
                  </li>
                </Fragment>
              )
            })}
            {drag && overSlot === sel.length && <li aria-hidden className="h-1 rounded bg-green" />}
          </ol>
          <div className={`${endLabel} border-t border-dashed border-line`}>SOUTH</div>
          <div className="mt-3 flex items-center justify-between border-t border-line pt-2 text-sm">
            <span className="font-semibold">Total seats</span>
            <span className="font-bold tabular-nums" data-seat-total={totalSeats}>{totalSeats}</span>
          </div>
          {unknownSeats > 0 && (
            <div className="mt-1 text-[11px] text-ink-soft">
              {unknownSeats} carriage{unknownSeats === 1 ? '' : 's'} with no seat count in the catalogue — not included in the total.
            </div>
          )}
          {!archived && sel.length > 0 && (
            <div className="mt-3 text-[11px] text-ink-soft">
              Letters run A, B, C… from the north end, so two carriages of the same type are told apart. Drag to re-order, or use ▲ ▼. Use the ticket button on a carriage to choose which ticket types sell on it.
            </div>
          )}
        </section>

        {/* Carriage ticket assignments */}
        <section aria-label="Carriage Ticket Assignments" className="rounded border border-line p-3 md:col-span-2 xl:col-span-1">
          <div className="text-xs font-semibold uppercase tracking-wide text-ink-soft mb-2">Carriage Ticket Assignments</div>
          {withAssignments.length === 0 ? (
            <p className="text-xs text-ink-soft italic py-3 text-center">
              No tickets assigned yet{archived ? '.' : ' — use the ticket button on a carriage in the formation.'}
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              {withAssignments.map(({ c: inst, i }) => {
                const allocated = inst.tickets.reduce((a, t) => a + t.quantity, 0)
                const full = inst.seats != null && allocated >= inst.seats
                return (
                  <div key={inst.id} className="rounded border border-line" data-assignment-carriage={inst.id}>
                    <div className="flex items-center gap-2 px-3 py-1.5 border-b border-line bg-parchment/40">
                      <span className="min-w-[1.5rem] text-center text-xs font-bold text-green bg-green-soft rounded px-1 py-0.5">{formationLetter(i)}</span>
                      <span className="text-sm font-semibold">{inst.code}</span>
                      <span className="text-xs text-ink-soft">{inst.seats != null ? `${inst.seats} seats` : 'seats unknown'}</span>
                    </div>
                    <ul className="px-3 py-1">
                      {inst.tickets.map((t) => {
                        const key = ticketKey(t)
                        const draftKey = `${inst.id}:${key}`
                        const others = allocated - t.quantity
                        const max = inst.seats != null ? Math.max(0, inst.seats - others) : undefined
                        return (
                          <li key={key} className="flex items-center gap-2 py-1.5 text-sm border-b border-dashed border-line last:border-b-0" data-assignment={key} data-source={t.source}>
                            <span className="flex-1 min-w-0">
                              <span className="font-semibold">{t.name}</span>
                              {t.source === 'merac' && <MeracTag />}
                              {t.pricePence != null && <span className="text-ink-soft"> · {formatGBP(t.pricePence)}</span>}
                            </span>
                            {archived ? (
                              <span className="w-16 text-right tabular-nums" data-qty={t.quantity}>{t.quantity}</span>
                            ) : (
                              <input
                                type="number"
                                min={0}
                                max={max}
                                step={1}
                                inputMode="numeric"
                                aria-label={`Seats for ${t.name} on ${carriageLabel(inst, i)}`}
                                className="w-16 rounded border border-line px-2 py-0.5 text-right text-sm tabular-nums"
                                value={qtyDraft[draftKey] ?? String(t.quantity)}
                                onChange={(e) => setQtyDraft((d) => ({ ...d, [draftKey]: e.target.value }))}
                                onBlur={(e) => commitQuantity(inst, key, e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
                                data-qty-input={`${inst.id}:${key}`}
                              />
                            )}
                            {!archived && (
                              <button type="button" onClick={() => removeAssignment(inst, key)} aria-label={`Remove ${t.name} from ${carriageLabel(inst, i)}`} title="Remove this ticket from the carriage" className="px-1 text-ink-soft hover:text-oxblood leading-none">✕</button>
                            )}
                          </li>
                        )
                      })}
                    </ul>
                    <div className={`flex items-center justify-between px-3 py-1.5 border-t border-line text-xs ${full ? 'text-green font-semibold' : 'text-ink-soft'}`} data-carriage-allocated={allocated}>
                      <span>Allocated</span>
                      <span className="tabular-nums">{inst.seats != null ? `${allocated} of ${inst.seats} allocated` : `${allocated} allocated`}</span>
                    </div>
                  </div>
                )
              })}

              {/* Totals across every carriage, per ticket */}
              <div className="rounded border border-green/40 bg-green-soft/20" data-ticket-totals>
                <div className="px-3 py-1.5 border-b border-line text-xs font-semibold uppercase tracking-wide text-ink-soft">All carriages — by ticket</div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[11px] uppercase tracking-wide text-ink-soft">
                      <th className="text-left px-3 py-1 font-semibold">Ticket</th>
                      <th className="text-right px-2 py-1 font-semibold">Seats</th>
                      <th className="text-right px-2 py-1 font-semibold">Price</th>
                      <th className="text-right px-3 py-1 font-semibold">Max revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ticketTotals.map((t) => (
                      <tr key={t.key} className="border-t border-dashed border-line" data-total-ticket={t.key} data-total-qty={t.qty}>
                        <td className="px-3 py-1">{t.name}{t.source === 'merac' && <MeracTag />}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{t.qty}</td>
                        <td className="px-2 py-1 text-right tabular-nums text-ink-soft">{t.price != null ? formatGBP(t.price) : '—'}</td>
                        <td className="px-3 py-1 text-right tabular-nums font-semibold">{t.revenue != null ? formatGBP(t.revenue) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-line font-bold">
                      <td className="px-3 py-1.5">Total seats allocated by tickets</td>
                      <td className="px-2 py-1.5 text-right tabular-nums" data-seats-allocated={seatsAllocated}>{seatsAllocated}</td>
                      <td className="px-2 py-1.5 text-right text-ink-soft text-xs font-normal">of {totalSeats}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums" data-revenue-total={revenueTotal}>{anyPriced ? formatGBP(revenueTotal) : '—'}</td>
                    </tr>
                  </tfoot>
                </table>
                <div className="px-3 pb-2 text-[11px] text-ink-soft">Max revenue = seats allocated × the ticket price — the ticket build's price, or for a MERAC ticket the average price it has sold at so far; an estimate if every allocated seat sells.</div>
              </div>
            </div>
          )}
          {qtyNote && <div className="mt-2 text-xs text-oxblood">{qtyNote}</div>}
        </section>
      </div>

      {saveError && (
        <div className="px-4 py-2 text-xs text-oxblood border-t border-line">
          {saveError}
        </div>
      )}

      {assigning !== null && sel[assigning] && (
        <AssignTicketsDialog
          eventId={eventId}
          label={carriageLabel(sel[assigning]!, assigning)}
          seats={sel[assigning]!.seats}
          current={sel[assigning]!.tickets}
          saving={ticketsMut.isPending}
          error={assignError}
          onSave={(keys) => saveAssignments(sel[assigning]!, keys)}
          onClose={() => { setAssigning(null); setAssignError(null) }}
        />
      )}
    </div>
  )
}

